import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  getNewsletterDraft, getNewsletterSourcesList, saveNewsletterDraft, previewNewsletter2, suggestNewsletterAi, type NlAiKind,
} from "@/lib/newsletter2.functions";
import { sendResendTest } from "@/lib/newsletter2-resend.functions";
import { SendPanel } from "@/components/admin/NewsletterSendPanel";
import { BLOCK_LABEL, DEFAULT_BLOCKS, blocksSchema, newBlock, type Block, type BlockType } from "@/lib/newsletter2/blocks";

export const Route = createFileRoute("/admin/bulten/$id")({
  head: () => ({ meta: [{ title: "Bülten düzenle – Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: DraftEditor,
});

type Meta = { title: string; subject: string; preheader: string; issueNumber: number | null };
const input = "w-full border border-border rounded-md bg-paper px-3 py-2 text-sm";

function DraftEditor() {
  const { id } = Route.useParams();
  const get = useServerFn(getNewsletterDraft);
  const srcFn = useServerFn(getNewsletterSourcesList);
  const draft = useQuery({ queryKey: ["nl2-draft", id], queryFn: () => get({ data: { id } }) });
  const src = useQuery({ queryKey: ["nl2-sources"], queryFn: () => srcFn(), staleTime: 300_000 });
  if (draft.isLoading || src.isLoading) return <p className="text-sm text-ink/50">Yükleniyor…</p>;
  if (draft.error || !draft.data) return <p className="text-sm text-destructive">Bülten açılamadı.</p>;
  return <Editor id={id} row={draft.data as any} sources={src.data!} />;
}

type Src = Awaited<ReturnType<typeof getNewsletterSourcesList>>;

function Editor({ id, row, sources }: { id: string; row: any; sources: Src }) {
  const save = useServerFn(saveNewsletterDraft);
  const preview = useServerFn(previewNewsletter2);
  const sendTest = useServerFn(sendResendTest);
  const qc = useQueryClient();
  const ai = useServerFn(suggestNewsletterAi);
  const locked = row.status !== "draft";
  const [meta, setMeta] = useState<Meta>({ title: row.title, subject: row.subject, preheader: row.preheader, issueNumber: row.issue_number });
  const [blocks, setBlocks] = useState<Block[]>(() => blocksSchema.catch([]).parse(row.blocks));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [html, setHtml] = useState("");
  const [empty, setEmpty] = useState<string[]>([]);
  const [sizeKb, setSizeKb] = useState(0);
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const [tab, setTab] = useState<"edit" | "preview">("edit");
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [aiBusy, setAiBusy] = useState<NlAiKind | null>(null);
  const [aiResult, setAiResult] = useState<{ kind: NlAiKind; result: any } | null>(null);

  useEffect(() => { supabase.auth.getUser().then(({ data }) => setTestTo((t) => t || data.user?.email || "")); }, []);

  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      try {
        const r = await preview({ data: { subject: meta.subject, preheader: meta.preheader, issueNumber: meta.issueNumber, blocks } });
        setHtml(r.html); setEmpty(r.empty); setSizeKb(r.sizeKb);
      } catch { /* preview is best effort */ }
    }, 500);
    return () => window.clearTimeout(timer.current);
  }, [meta, blocks, preview]);

  const upd = (bid: string, patch: Partial<Block>) => { setBlocks((bs) => bs.map((b) => (b.id === bid ? ({ ...b, ...patch } as Block) : b))); setDirty(true); };
  const move = (i: number, d: -1 | 1) => { setBlocks((bs) => { const n = [...bs]; const j = i + d; if (j < 0 || j >= n.length) return bs; [n[i], n[j]] = [n[j], n[i]]; return n; }); setDirty(true); };
  const remove = (bid: string) => { setBlocks((bs) => bs.filter((b) => b.id !== bid)); setDirty(true); };
  const add = (t: BlockType) => { setBlocks((bs) => [...bs, newBlock(t)]); setDirty(true); };
  const setM = (p: Partial<Meta>) => { setMeta((m) => ({ ...m, ...p })); setDirty(true); };

  async function onSave() {
    setSaving(true);
    try { await save({ data: { id, ...meta, blocks } }); setDirty(false); qc.invalidateQueries({ queryKey: ["nl2-draft", id] }); toast.success("Taslak kaydedildi"); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Kaydedilemedi"); }
    finally { setSaving(false); }
  }
  async function onTest() {
    if (dirty) await onSave();
    if (!window.confirm(`Test e-postası yalnızca ${testTo} adresine gönderilecek. Devam edilsin mi?`)) return;
    setTesting(true);
    try {
      const r = await sendTest({ data: { id, to: testTo } });
      qc.invalidateQueries({ queryKey: ["nl2-draft", id] });
      toast.success(r.sender === "domain" ? `Test ${testTo} adresine gönderildi` : `Test ${testTo} adresine Resend test göndericisiyle gönderildi`);
    } catch (e) { toast.error(e instanceof Error ? e.message : "Test gönderilemedi"); }
    finally { setTesting(false); }
  }
  async function runAi(kind: NlAiKind) {
    if (dirty) await onSave();
    setAiBusy(kind);
    try { const r = await ai({ data: { id, kind } }); setAiResult({ kind, result: r.result }); }
    catch (e) { toast.error(e instanceof Error ? e.message : "AI önerisi alınamadı"); }
    finally { setAiBusy(null); }
  }
  const firstOf = (t: BlockType) => blocks.find((b) => b.type === t);

  const missing = DEFAULT_BLOCKS.filter((t) => !blocks.some((b) => b.type === t));

  const editPane = (
    <div className="space-y-6">
      <section className="space-y-3">
        <Field label="Konu satırı"><input className={input} value={meta.subject} maxLength={200} disabled={locked} onChange={(e) => setM({ subject: e.target.value })} /></Field>
        <Field label="Ön metin (gelen kutusunda konunun yanında)"><input className={input} value={meta.preheader} maxLength={200} disabled={locked} onChange={(e) => setM({ preheader: e.target.value })} /></Field>
        <div className="grid grid-cols-[minmax(0,1fr)_7rem] gap-3">
          <Field label="İç ad (yalnız panelde)"><input className={input} value={meta.title} maxLength={200} disabled={locked} onChange={(e) => setM({ title: e.target.value })} /></Field>
          <Field label="Sayı no"><input type="number" min={1} className={input} value={meta.issueNumber ?? ""} disabled={locked} onChange={(e) => setM({ issueNumber: e.target.value ? Number(e.target.value) : null })} /></Field>
        </div>
        <AiButton kind="subject" label="Konu satırı öner" busy={aiBusy} onRun={runAi} />
      </section>

      {blocks.map((b, i) => (
        <section key={b.id} className={`border rounded-lg p-4 space-y-3 ${empty.includes(b.id) ? "border-dashed border-border" : "border-border"}`}>
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
            <h3 className="min-w-0 truncate font-serif text-lg">{BLOCK_LABEL[b.type]}{empty.includes(b.id) && <span className="ml-2 text-xs font-sans text-ink/50">boş — e-postada görünmez</span>}</h3>
            {!locked && <div className="shrink-0 flex gap-1 text-xs">
              <button aria-label="Yukarı taşı" onClick={() => move(i, -1)} className="px-2 py-1 border border-border rounded">↑</button>
              <button aria-label="Aşağı taşı" onClick={() => move(i, 1)} className="px-2 py-1 border border-border rounded">↓</button>
              <button aria-label="Bloğu kaldır" onClick={() => remove(b.id)} className="px-2 py-1 border border-border rounded">Kaldır</button>
            </div>}
          </div>
          <BlockFields b={b} upd={upd} sources={sources} locked={locked} />
          {b.type === "editor" && <div className="flex flex-wrap gap-2"><AiButton kind="intro" label="Giriş taslağı öner" busy={aiBusy} onRun={runAi} /><AiButton kind="polish" label="Tekrar ve okunabilirlik" busy={aiBusy} onRun={runAi} /></div>}
          {b.type === "featured" && <AiButton kind="summaries" label="Bültene özel özet öner" busy={aiBusy} onRun={runAi} />}
          {b.type === "book" && <AiButton kind="book_link" label="Kitap–konu bağlantısı öner" busy={aiBusy} onRun={runAi} />}
        </section>
      ))}

      {!locked && (
        <section className="flex flex-wrap gap-2">
          {(missing.length ? missing : (["editor", "reflection"] as BlockType[])).map((t) => (
            <button key={t} onClick={() => add(t)} className="px-3 py-1.5 border border-border rounded-full text-xs">+ {BLOCK_LABEL[t]}</button>
          ))}
        </section>
      )}

      {aiResult && <AiPanel r={aiResult} onClose={() => setAiResult(null)} apply={(fn) => { fn(); setAiResult(null); setDirty(true); toast.success("Öneri editöre uygulandı; kaydetmeyi unutmayın"); }}
        actions={{
          text: (t) => { const k = aiResult.kind === "book_link" ? "book" : "editor"; const blk = firstOf(k); if (blk) upd(blk.id, (k === "book" ? { note: t } : { text: t }) as any); },
          subject: (o) => { setM({ subject: o.subject, preheader: o.preheader }); const hb = firstOf("header"); if (hb && o.issueTitle) upd(hb.id, { issueTitle: o.issueTitle } as any); },
          summary: (pid, t) => { const f = blocks.find((x) => x.type === "featured" && x.postId === pid); if (f) upd(f.id, { note: t } as any); },
          postTitle: (pid) => sources.posts.find((p) => p.id === pid)?.title ?? pid,
        }} />}
    </div>
  );

  const previewPane = (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs">
        <button onClick={() => setDevice("desktop")} className={`px-3 py-1 rounded-full border ${device === "desktop" ? "bg-ink text-paper border-ink" : "border-border"}`}>Masaüstü</button>
        <button onClick={() => setDevice("mobile")} className={`px-3 py-1 rounded-full border ${device === "mobile" ? "bg-ink text-paper border-ink" : "border-border"}`}>Mobil</button>
        <span className="ml-auto text-ink/50">{sizeKb} KB{sizeKb > 100 ? " · Gmail kırpabilir" : ""}</span>
      </div>
      <div className="border border-border rounded-lg bg-background overflow-hidden flex justify-center">
        <iframe title="Bülten önizlemesi" srcDoc={html} sandbox="" className="h-[70vh] bg-background" style={{ width: device === "mobile" ? 375 : "100%" }} />
      </div>
    </div>
  );

  return (
    <div className="pb-28">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 mb-5">
        <div className="min-w-0">
          <Link to="/admin/bulten" className="text-xs text-ink/55">← Bülten</Link>
          <h1 className="font-serif text-2xl truncate">{meta.subject || "Adsız bülten"}</h1>
        </div>
        {!locked && <button onClick={onSave} disabled={saving || !dirty} className="shrink-0 px-4 py-2 bg-ink text-paper rounded-full text-sm disabled:opacity-40">{saving ? "Kaydediliyor…" : dirty ? "Kaydet" : "Kaydedildi"}</button>}
      </div>

      <div className="lg:hidden flex gap-2 mb-4 text-sm">
        <button onClick={() => setTab("edit")} className={`flex-1 py-2 rounded-full border ${tab === "edit" ? "bg-ink text-paper border-ink" : "border-border"}`}>Düzenle</button>
        <button onClick={() => setTab("preview")} className={`flex-1 py-2 rounded-full border ${tab === "preview" ? "bg-ink text-paper border-ink" : "border-border"}`}>Önizleme</button>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className={tab === "edit" ? "" : "hidden lg:block"}>{editPane}</div>
        <div className={`${tab === "preview" ? "" : "hidden lg:block"} lg:sticky lg:top-28 self-start`}>
          {previewPane}
          <section className="mt-5 border border-border rounded-lg p-4 space-y-3">
            <h2 className="font-serif text-lg">Test e-postası</h2>
            <p className="text-xs text-ink/60">Yalnızca yazdığınız tek adrese gider. Abonelere gönderim bu ekranda yok.</p>
            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
              <input type="email" className={input} value={testTo} onChange={(e) => setTestTo(e.target.value)} aria-label="Test adresi" />
              <button onClick={onTest} disabled={testing || !testTo || !meta.subject.trim()} className="shrink-0 px-4 py-2 border border-ink rounded-full text-sm disabled:opacity-40">{testing ? "Gönderiliyor…" : "Test gönder"}</button>
            </div>
            {row.last_test_at && <p className="text-xs text-ink/50">Son test: {row.last_test_to} · {new Date(row.last_test_at).toLocaleString("tr-TR")}</p>}
          </section>
          <SendPanel id={id} status={row.status} dirty={dirty} lastTestAt={row.last_test_at} updatedAt={row.updated_at} scheduledAt={row.scheduled_at} resendStatus={row.resend_status} />
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="block text-xs text-ink/60 mb-1">{label}</span>{children}</label>;
}

function AiButton({ kind, label, busy, onRun }: { kind: NlAiKind; label: string; busy: NlAiKind | null; onRun: (k: NlAiKind) => void }) {
  return <button onClick={() => onRun(kind)} disabled={!!busy} className="px-3 py-1.5 rounded-full border border-border text-xs text-ink/75 disabled:opacity-40">{busy === kind ? "AI düşünüyor…" : `AI: ${label}`}</button>;
}

function Select({ value, onChange, options, disabled, placeholder }: { value: string | null; onChange: (v: string | null) => void; options: { id: string; title: string }[]; disabled?: boolean; placeholder: string }) {
  return (
    <select className={input} value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
    </select>
  );
}

function BlockFields({ b, upd, sources, locked }: { b: Block; upd: (id: string, p: Partial<Block>) => void; sources: Src; locked: boolean }) {
  switch (b.type) {
    case "header": return <Field label="Bu sayıya özel kısa başlık (isteğe bağlı)"><input className={input} value={b.issueTitle} disabled={locked} onChange={(e) => upd(b.id, { issueTitle: e.target.value })} /></Field>;
    case "editor": return <Field label="Kişisel giriş (paragrafları boş satırla ayırın)"><textarea className={`${input} min-h-40`} value={b.text} disabled={locked} onChange={(e) => upd(b.id, { text: e.target.value })} /></Field>;
    case "reflection": return <Field label="Kısa soru, gözlem veya düşünce"><textarea className={`${input} min-h-20`} value={b.text} disabled={locked} onChange={(e) => upd(b.id, { text: e.target.value })} /></Field>;
    case "closing": return <Field label="İmza"><textarea className={`${input} min-h-16`} value={b.signature} disabled={locked} onChange={(e) => upd(b.id, { signature: e.target.value })} /></Field>;
    case "featured": return (<>
      <Select value={b.postId} disabled={locked} placeholder="Yazı seçin" options={sources.posts} onChange={(v) => upd(b.id, { postId: v })} />
      <Field label="Bültene özel açıklama (boşsa yazının özeti kullanılır)"><textarea className={`${input} min-h-20`} value={b.note} disabled={locked} onChange={(e) => upd(b.id, { note: e.target.value })} /></Field>
      <Field label="Kapak alternatif metni (boşsa kayıtlı metin)"><input className={input} value={b.imageAlt} disabled={locked} onChange={(e) => upd(b.id, { imageAlt: e.target.value })} /></Field>
    </>);
    case "posts": return (<div className="space-y-2">
      {[0, 1, 2].map((i) => (
        <Select key={i} value={b.postIds[i] ?? null} disabled={locked || (i > 0 && !b.postIds[i - 1])} placeholder={`${i + 1}. yazı (isteğe bağlı)`} options={sources.posts}
          onChange={(v) => { const n = [...b.postIds]; if (v) n[i] = v; else n.splice(i, 1); upd(b.id, { postIds: n.filter(Boolean).slice(0, 3) }); }} />
      ))}
    </div>);
    case "concept": return (<>
      <Select value={b.conceptId} disabled={locked} placeholder="Kavram seçin" options={sources.concepts} onChange={(v) => upd(b.id, { conceptId: v })} />
      <Field label="Bültene özel açıklama (boşsa sözlükteki kısa tanım)"><textarea className={`${input} min-h-16`} value={b.note} disabled={locked} onChange={(e) => upd(b.id, { note: e.target.value })} /></Field>
    </>);
    case "book": return (<>
      <Select value={b.bookKey} disabled={locked} placeholder="Kitap seçin" options={sources.books} onChange={(v) => upd(b.id, { bookKey: v })} />
      <Field label="Bültene özel tanıtım (boşsa kitap sayfasındaki özet)"><textarea className={`${input} min-h-16`} value={b.note} disabled={locked} onChange={(e) => upd(b.id, { note: e.target.value })} /></Field>
    </>);
  }
}

type Acts = { text: (t: string) => void; subject: (o: any) => void; summary: (pid: string, t: string) => void; postTitle: (pid: string) => string };

function AiPanel({ r, onClose, apply, actions }: { r: { kind: NlAiKind; result: any }; onClose: () => void; apply: (fn: () => void) => void; actions: Acts }) {
  const res = r.result ?? {};
  return (
    <section className="border border-ink/30 rounded-lg p-4 space-y-3 bg-ink/[0.02]">
      <div className="flex items-center justify-between"><h3 className="font-serif text-lg">AI önerisi</h3><button onClick={onClose} className="text-xs text-ink/60">Reddet ve kapat</button></div>
      <p className="text-xs text-ink/55">Yalnız öneri. Uygulamadığınız hiçbir şey değişmez; kaynak yazılar hiç değişmez.</p>
      {typeof res.text === "string" && (res.text ? <>
        <p className="whitespace-pre-wrap text-sm leading-relaxed">{res.text}</p>
        {Array.isArray(res.notes) && <ul className="text-xs text-ink/60 list-disc pl-5">{res.notes.map((n: string, i: number) => <li key={i}>{n}</li>)}</ul>}
        <button onClick={() => apply(() => actions.text(res.text))} className="px-3 py-1.5 bg-ink text-paper rounded-full text-xs">Uygula</button>
      </> : <p className="text-sm text-ink/60">Güvenli öneri üretilemedi (doğrulanamayan sayı içerdiği için elendi).</p>)}
      {Array.isArray(res.options) && res.options.map((o: any, i: number) => (
        <div key={i} className="border-t border-border pt-2 text-sm"><div className="font-medium">{o.subject}</div><div className="text-ink/60">{o.preheader}</div>{o.issueTitle && <div className="text-xs text-ink/50">Sayı başlığı: {o.issueTitle}</div>}
          <button onClick={() => apply(() => actions.subject(o))} className="mt-1 px-3 py-1 bg-ink text-paper rounded-full text-xs">Uygula</button></div>
      ))}
      {Array.isArray(res.items) && res.items.map((it: any) => (
        <div key={it.postId} className="border-t border-border pt-2 text-sm"><div className="text-xs text-ink/55">{actions.postTitle(it.postId)}</div><p>{it.text}</p>
          <button onClick={() => apply(() => actions.summary(it.postId, it.text))} className="mt-1 px-3 py-1 bg-ink text-paper rounded-full text-xs">Uygula</button></div>
      ))}
    </section>
  );
}
